/* ============================================================
   ProductPage — product detail + add to cart
   ============================================================ */

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { ProductDetail, CartItemView } from '../types/product';
import { formatRub } from '../types/product';
import { ReviewsBlock } from './ReviewsBlock';
import { ProductReviewsBlock } from './ProductReviewsBlock';
import { MediaGallery } from './MediaGallery';
import { heartLabel } from './favoritesView';
import {
  computeOptionPricing,
  optionLineTotal,
  optionSummary,
  optionUnitPrice,
  selectedColorLabel,
  toWireSelections,
  type OptionSelection,
} from './productOptionsView';
import { meshColor } from './model3dView';
import { mediaGalleryView } from './mediaGalleryView';
import { addFailureFallback, sellerLineForProduct } from './cartAddView';

interface Props {
  productId: string;
  onBack: () => void;
  onGoToCart: () => void;
  onNeedAuth: () => void;
}

export function ProductPage({ productId, onBack, onGoToCart, onNeedAuth }: Props) {
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // CB-6 (a90f4a83): heart state for this product (authenticated users).
  const [favorited, setFavorited] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  // a1a3b8f4: seller-option selections (optionId → choice value / qty / on).
  const [selections, setSelections] = useState<OptionSelection[]>([]);

  useEffect(() => {
    setError(null);
    setNotice(null);
    api.getProduct(productId)
      .then((p) => {
        setProduct(p);
        setQuantity(p.minOrderQuantity);
        setSelections([]);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Ошибка загрузки'));
  }, [productId]);

  useEffect(() => {
    // CB-6: heart state rides the compact id fetch — one call per page view.
    if (!isAuthenticated) {
      setFavorited(false);
      return;
    }
    api.getFavoriteIds()
      .then((ids) => setFavorited((ids.productIds ?? []).includes(productId)))
      .catch(() => {});
  }, [productId, isAuthenticated]);

  if (error) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-2xl)' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--color-danger)' }}>{error}</p>
          <button className="btn btn-secondary" onClick={onBack} style={{ marginTop: 'var(--space-md)' }}>
            ← В магазин
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-2xl)', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  // a1a3b8f4: live option pricing + the color feed for the viewer card.
  const optionPricing = computeOptionPricing(product.options, selections);
  const liveUnit = optionUnitPrice(product.priceRub, optionPricing);
  const liveTotal = optionLineTotal(product.priceRub, optionPricing, quantity);
  const optionsSummaryText = optionSummary(optionPricing);
  const colorChoice = selectedColorLabel(product.options, selections);

  const outOfStock = product.stockLeft !== null && product.stockLeft <= 0;
  const overMax = product.maxOrderQuantity !== null && quantity > product.maxOrderQuantity;
  const overStock = product.stockLeft !== null && quantity > product.stockLeft;
  const belowMin = quantity < product.minOrderQuantity;
  const invalidQty = belowMin || overMax || overStock;

  const handleAdd = async () => {
    if (!isAuthenticated) {
      onNeedAuth();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.addToCart(product.productId, quantity, toWireSelections(selections));
      setNotice(`Добавлено в корзину: ${quantity} × ${product.title}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось добавить';
      // 01d4a604: the server merges quantities (dto.Quantity + existing line),
      // so re-adding the last unit of a stock-1 product that is already in the
      // cart fails with 400 while the line stays there. Resolve the failure
      // against a live cart fetch: if the product is in the cart, flip to the
      // «Перейти в корзину» state instead of a dead-end red error.
      let line: CartItemView | null = null;
      try { line = sellerLineForProduct(await api.getCart(), product.productId); } catch { /* cart fetch failed — plain error */ }
      const fallback = addFailureFallback(message, line);
      if (fallback.kind === 'already-in-cart') {
        setError(null);
        setNotice(fallback.message);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      onNeedAuth();
      return;
    }
    setFavoriteBusy(true);
    setError(null);
    try {
      const result = await api.toggleProductFavorite(product.productId);
      setFavorited(result.favorited);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось обновить избранное');
    } finally {
      setFavoriteBusy(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)', maxWidth: 960 }}>
      <button
        onClick={onBack}
        style={{ color: 'var(--color-primary)', marginBottom: 'var(--space-md)', fontSize: 'var(--fs-body)' }}
      >
        ← В магазин
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--space-lg)' }}>
        <div className="card" style={{ padding: 'var(--space-lg)' }}>
          {/* cca635b3: model rows mount the interactive 3D stage, tinted with
              the buyer's chosen color option (else the product's base color).
              The no-WebGL/load-failure fallback is the first raster photo. */}
          <MediaGallery
            product={product}
            model3dTint={colorChoice ?? product.color}
            model3dFallbackUrl={
              mediaGalleryView(product).items.find((i) => i.kind === 'image')?.thumbUrl
                ?? product.imageUrl
                ?? null
            }
          />

          <h1 style={{ fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', letterSpacing: '-0.02em' }}>
            {product.title}
          </h1>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-md)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
              Исполнитель: {product.providerName}
            </p>
            <button
              className="btn btn-secondary"
              onClick={() => void toggleFavorite()}
              disabled={favoriteBusy}
              style={{ flexShrink: 0, color: favorited ? 'var(--color-primary)' : undefined }}
            >
              {favoriteBusy ? '…' : heartLabel(favorited)}
            </button>
          </div>

          {product.description && (
            <p style={{ marginTop: 'var(--space-md)', fontSize: 'var(--fs-body)', lineHeight: 1.6 }}>
              {product.description}
            </p>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', marginTop: 'var(--space-md)' }}>
            <span className="badge">{product.materialName}</span>
            <span className="badge">{product.color}</span>
            <span className="badge">Срок: {product.productionDays} дн.</span>
            {product.stockLeft === null ? (
              <span className="badge badge-success">Печатается под заказ</span>
            ) : product.stockLeft > 0 ? (
              <span className="badge badge-success">В наличии: {product.stockLeft}</span>
            ) : (
              <span className="badge badge-danger">Нет в наличии</span>
            )}
          </div>

          {/* a1a3b8f4: seller-defined priced options picker */}
          {(product.options ?? []).length > 0 && (
            <div
              style={{
                marginTop: 'var(--space-md)',
                paddingTop: 'var(--space-md)',
                borderTop: '1px solid var(--color-separator)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-sm)',
              }}
            >
              {(product.options ?? []).map((option) => {
                const selection = selections.find((s2) => s2.optionId === option.optionId);
                const isColorChoice = option.type === 1 && /цвет|color/i.test(option.name);
                return (
                  <div key={option.optionId} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                    <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
                      {option.name}
                    </span>

                    {option.type === 0 && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-xs)',
                          cursor: 'pointer',
                          fontSize: 'var(--fs-body)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(selection)}
                          onChange={(e) =>
                            setSelections((prev) =>
                              e.target.checked
                                ? [...prev, { optionId: option.optionId }]
                                : prev.filter((s2) => s2.optionId !== option.optionId),
                            )
                          }
                        />
                        {option.name}
                        {option.priceRub > 0 && (
                          <span style={{ color: 'var(--color-text-tertiary)' }}>
                            +{option.priceRub} ₽{option.priceMode === 1 ? '/шт.' : ''}
                          </span>
                        )}
                      </label>
                    )}

                    {option.type === 1 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                        {(option.values ?? []).map((value) => {
                          const active = selection?.valueId === value.valueId;
                          return (
                            <button
                              key={value.valueId}
                              className={active ? 'btn btn-primary' : 'btn btn-secondary'}
                              style={{ padding: '6px 12px', fontSize: 'var(--fs-caption)' }}
                              onClick={() =>
                                setSelections((prev) =>
                                  active
                                    ? prev.filter((s2) => s2.optionId !== option.optionId)
                                    : [
                                        ...prev.filter((s2) => s2.optionId !== option.optionId),
                                        { optionId: option.optionId, valueId: value.valueId },
                                      ],
                                )
                              }
                            >
                              {isColorChoice && (
                                <span
                                  aria-hidden
                                  style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background: meshColor({ colorName: value.label }),
                                    marginRight: 6,
                                    border: '1px solid var(--color-separator)',
                                  }}
                                />
                              )}
                              {value.label}
                              {value.priceRub > 0 && ` (+${value.priceRub} ₽${option.priceMode === 1 ? '/шт.' : ''})`}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {option.type === 2 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ width: 36, height: 36, padding: 0 }}
                          aria-label="Меньше"
                          onClick={() =>
                            setSelections((prev) => {
                              const current = prev.find((s2) => s2.optionId === option.optionId);
                              const next = Math.max(1, (current?.quantity ?? 1) - 1);
                              return current
                                ? next === 1 && current.quantity === 1
                                  ? prev
                                  : prev.map((s2) => (s2.optionId === option.optionId ? { ...s2, quantity: next } : s2))
                                : [...prev, { optionId: option.optionId, quantity: next }];
                            })
                          }
                        >
                          −
                        </button>
                        <span style={{ minWidth: 28, textAlign: 'center', fontSize: 'var(--fs-body)' }}>
                          {selection?.quantity ?? 0}
                        </span>
                        <button
                          className="btn btn-secondary"
                          style={{ width: 36, height: 36, padding: 0 }}
                          aria-label="Больше"
                          onClick={() =>
                            setSelections((prev) => {
                              const current = prev.find((s2) => s2.optionId === option.optionId);
                              const next = Math.min(100, (current?.quantity ?? 0) + 1);
                              return current
                                ? prev.map((s2) => (s2.optionId === option.optionId ? { ...s2, quantity: next } : s2))
                                : [...prev, { optionId: option.optionId, quantity: next }];
                            })
                          }
                        >
                          +
                        </button>
                        <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--fs-caption)' }}>
                          +{option.priceRub} ₽/шт.
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Price + qty + add to cart */}
          <div
            style={{
              marginTop: 'var(--space-lg)',
              paddingTop: 'var(--space-lg)',
              borderTop: '1px solid var(--color-separator)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--space-md)',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)' }}>
                <span style={{ fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
                  {formatRub(liveUnit)}
                </span>
                {product.compareAtPriceRub && (
                  <span style={{ color: 'var(--color-text-tertiary)', textDecoration: 'line-through' }}>
                    {formatRub(product.compareAtPriceRub)}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                Мин. заказ: {product.minOrderQuantity}
                {product.maxOrderQuantity !== null && ` · макс.: ${product.maxOrderQuantity}`}
                {quantity > 1 && ` · итого: ${formatRub(liveTotal)}`}
              </div>
              {optionsSummaryText && (
                <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                  {optionsSummaryText}
                </div>
              )}
              {colorChoice && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  <span
                    aria-hidden
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: meshColor({ colorName: colorChoice }),
                      border: '1px solid var(--color-separator)',
                      display: 'inline-block',
                    }}
                  />
                  {colorChoice}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', marginLeft: 'auto' }}>
              <button
                className="btn btn-secondary"
                style={{ width: 44, height: 44, padding: 0 }}
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label="Уменьшить"
              >
                −
              </button>
              <input
                type="number"
                min={product.minOrderQuantity}
                max={product.maxOrderQuantity ?? product.stockLeft ?? undefined}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                style={{
                  width: 72,
                  height: 44,
                  textAlign: 'center',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-separator)',
                  background: 'var(--color-surface)',
                  fontSize: 'var(--fs-body)',
                }}
              />
              <button
                className="btn btn-secondary"
                style={{ width: 44, height: 44, padding: 0 }}
                onClick={() => setQuantity((q) => q + 1)}
                aria-label="Увеличить"
              >
                +
              </button>
            </div>

            <button
              className="btn btn-primary"
              disabled={outOfStock || invalidQty || busy}
              onClick={() => void handleAdd()}
              style={{ flex: '1 1 200px' }}
            >
              {outOfStock ? 'Нет в наличии' : busy ? 'Добавляем…' : 'В корзину'}
            </button>
          </div>

          {belowMin && (
            <p style={{ marginTop: 'var(--space-sm)', fontSize: 'var(--fs-caption)', color: 'var(--color-warning, #FF9500)' }}>
              Минимальный заказ: {product.minOrderQuantity} шт.
            </p>
          )}
          {overMax && (
            <p style={{ marginTop: 'var(--space-sm)', fontSize: 'var(--fs-caption)', color: 'var(--color-warning, #FF9500)' }}>
              Максимум для заказа: {product.maxOrderQuantity} шт.
            </p>
          )}
          {overStock && !outOfStock && (
            <p style={{ marginTop: 'var(--space-sm)', fontSize: 'var(--fs-caption)', color: 'var(--color-warning, #FF9500)' }}>
              В наличии только {product.stockLeft} шт.
            </p>
          )}
          {error && (
            <p style={{ marginTop: 'var(--space-sm)', fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>
          )}
          {notice && (
            <div style={{ marginTop: 'var(--space-md)', display: 'flex', gap: 'var(--space-sm)', alignItems: 'center' }}>
              <span className="badge badge-success">✓ {notice}</span>
              <button onClick={onGoToCart} style={{ color: 'var(--color-primary)', fontSize: 'var(--fs-body)' }}>
                Перейти в корзину →
              </button>
            </div>
          )}
        </div>

        {/* TASK-179: seller reviews + ratings */}
        <ReviewsBlock providerId={product.providerId} providerName={product.providerName} />

        {/* 054819ad: product reviews — display-only v1 */}
        <ProductReviewsBlock productId={product.productId} />
      </div>
    </div>
  );
}
